/**
 * @module Filters
 * @description Collection filtering system: facet form submission, price range inputs,
 * active-filter removal, sort controls, and facet status display with view-transition support.
 * @requires @fc/sections
 * @requires @fc/component
 * @requires @fc/events
 * @requires @fc/utilities
 * @customElement facets-form
 * @customElement facet-inputs
 * @customElement price-facet
 * @customElement facet-clear
 * @customElement facet-remove
 * @customElement sort-control
 * @customElement facet-status
 */
import { sectionManager } from '@fc/sections';
import { FCElement } from '@fc/component';
import { FilterChangeEvent, FCEvents } from '@fc/events';
import { debounce, formatMoney, startViewTransition } from '@fc/utilities';
import { DialogCloseEvent, DialogOpenEvent } from '@fc/dialog';
import { formatFitMoney } from '@fc/money-formatting';

const SEARCH_QUERY = 'q';
const COUNTRY_QUERY = 'country';
const PRESERVED_QUERY_KEYS = [SEARCH_QUERY, 'type', 'options[prefix]', COUNTRY_QUERY];
const FILTER_DRAWER_ID = 'filters-drawer';
const FILTER_DRAWER_DIALOG_ID = 'filters-drawer-dialog';
const FILTER_DRAWER_TRIGGER_SELECTOR = `[aria-controls="${FILTER_DRAWER_ID}"], [aria-controls="${FILTER_DRAWER_DIALOG_ID}"]`;
const RESULTS_COUNT_STATUS_SELECTOR = '[data-fit-results-count-status]';

const FIT_RESULTS_SHELL_SELECTOR = '[data-fit-results-shell]';
const FIT_RESULTS_GRID_SELECTOR = '[data-fit-results-grid], .product-grid';

function getActiveSortValue(formElement, isMobile) {
  if (!(formElement instanceof HTMLFormElement)) return null;

  const dualSortControl = formElement.querySelector('sort-control[data-should-use-select-on-mobile="true"]');
  if (!(dualSortControl instanceof HTMLElement)) return null;

  if (isMobile) {
    const select = dualSortControl.querySelector('select[name="sort_by"]');
    return select instanceof HTMLSelectElement ? select.value : null;
  }

  const radio = dualSortControl.querySelector('input[name="sort_by"]:checked');
  return radio instanceof HTMLInputElement ? radio.value : null;
}

function normalizeSortParameter(params, formElement) {
  const sortValues = params.getAll('sort_by');
  if (sortValues.length <= 1) return;

  const activeSortValue = getActiveSortValue(formElement, window.innerWidth < 750) ?? sortValues[sortValues.length - 1];
  params.delete('sort_by');

  if (activeSortValue) {
    params.set('sort_by', activeSortValue);
  }
}

function createFilterURLParameters(
  formData,
  currentSearchParams = new URL(window.location.href).searchParams,
  formElement = null,
) {
  const params = new URLSearchParams(formData);

  if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
  if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
  params.delete('page');
  normalizeSortParameter(params, formElement);

  for (const key of PRESERVED_QUERY_KEYS) {
    const value = currentSearchParams.get(key);
    if (value && !params.has(key)) params.set(key, value);
  }

  return params;
}

function buildURLFromParameters(params) {
  const url = new URL(window.location.href);
  url.search = '';

  for (const [key, value] of params) {
    url.searchParams.append(key, value);
  }

  return url;
}

function getURLParametersFromURL(url) {
  return new URL(url, window.location.origin).searchParams;
}

function getResultsGrid(element) {
  return element?.closest(FIT_RESULTS_SHELL_SELECTOR)?.querySelector(FIT_RESULTS_GRID_SELECTOR) ?? null;
}

function setResultsGridLoading(element, loading) {
  const grid = getResultsGrid(element);
  if (!grid) return;
  grid.toggleAttribute('data-loading', loading);
  grid.setAttribute('aria-busy', String(loading));
}

function getSectionRoot(sectionId) {
  if (!sectionId) return null;
  const domId = sectionId.startsWith('shopify-section-') ? sectionId : `shopify-section-${sectionId}`;
  return document.getElementById(domId);
}

function announceResultsCount(sectionId) {
  const root = getSectionRoot(sectionId) ?? document;
  const status = root.querySelector(RESULTS_COUNT_STATUS_SELECTOR);
  if (!(status instanceof HTMLElement)) return;

  const message =
    status.dataset.resultsCountText?.trim() ||
    root.querySelector('[data-fit-results-count]')?.textContent?.trim() ||
    '';
  if (!message) return;

  status.textContent = '';
  requestAnimationFrame(() => {
    if (status.isConnected) {
      status.textContent = message;
    }
  });
}

function getFilterDrawer() {
  return document.getElementById(FILTER_DRAWER_ID);
}

function isFilterDrawerOpen() {
  const dialog = getFilterDrawer()?.querySelector('dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

function syncFilterDrawerTriggers(expanded = isFilterDrawerOpen()) {
  for (const trigger of document.querySelectorAll(FILTER_DRAWER_TRIGGER_SELECTOR)) {
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', String(Boolean(expanded)));
    }
  }
}

function isFilterDrawerEvent(event) {
  const target = event.target instanceof Element ? event.target : null;
  const dialog = event.detail?.dialog;

  return (
    target?.id === FILTER_DRAWER_ID ||
    target?.closest?.(`#${FILTER_DRAWER_ID}`) instanceof Element ||
    (dialog instanceof Element && dialog.closest(`#${FILTER_DRAWER_ID}`) instanceof Element)
  );
}

function bindFilterDrawerStateSync() {
  syncFilterDrawerTriggers();
  document.addEventListener(DialogOpenEvent.eventName, (event) => {
    if (isFilterDrawerEvent(event)) syncFilterDrawerTriggers(true);
  });
  document.addEventListener(DialogCloseEvent.eventName, (event) => {
    if (isFilterDrawerEvent(event)) syncFilterDrawerTriggers(false);
  });
  document.addEventListener('DOMContentLoaded', () => syncFilterDrawerTriggers());
}

// ── FacetsFormComponent ──

class FacetsFormComponent extends FCElement {
  requiredRefs = ['facetsForm'];

  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    return createFilterURLParameters(formData, new URL(window.location.href).searchParams, this.refs.facetsForm);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  #pushURL(params) {
    const url = buildURLFromParameters(params);
    history.pushState({ urlParameters: params.toString() }, '', url.toString());
  }

  updateFilters = () => {
    const params = this.createURLParameters();
    this.#pushURL(params);
    this.dispatchEvent(new FilterChangeEvent(params));
    this.#render();
  };

  #setLoading(loading) {
    setResultsGridLoading(this, loading);
  }

  #render() {
    const sid = this.sectionId;
    const useTransition = !this.closest('dialog');
    this.#setLoading(true);

    const renderPromise = useTransition
      ? startViewTransition(() => sectionManager.renderSection(sid), ['product-grid'])
      : sectionManager.renderSection(sid);

    renderPromise
      .then(() => {
        this.#setLoading(false);
        announceResultsCount(sid);
        syncFilterDrawerTriggers();
      })
      .catch(() => this.#setLoading(false));
  }

  updateFiltersByURL(url) {
    if (!url) return;
    const params = getURLParametersFromURL(url);
    history.pushState({ urlParameters: params.toString() }, '', url);
    this.dispatchEvent(new FilterChangeEvent(params));
    this.#render();
  }
}

if (!customElements.get('facets-form')) {
  customElements.define('facets-form', FacetsFormComponent);
}

// ── FacetInputsComponent ──

class FacetInputsComponent extends FCElement {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  updateFilters() {
    const facetsForm = this.closest('facets-form');
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFilters();
    this.#syncSummary();
  }

  handleKeyDown(event) {
    const input = event.target?.querySelector?.('input');
    if (!input) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.checked = !input.checked;
      this.updateFilters();
    }
  }

  prefetchPage = debounce((event) => {
    const form = this.closest('form');
    if (!form || !event.target?.querySelector) return;

    const inputEl = event.target.querySelector('input');
    if (!inputEl) return;

    const facetsForm = this.closest('facets-form');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!inputEl.checked) formData.append(inputEl.name, inputEl.value);

    const params = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);
    for (const [k, v] of params) url.searchParams.append(k, v);
    if (inputEl.checked) url.searchParams.delete(inputEl.name, inputEl.value);

    sectionManager.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  #syncSummary() {
    if (!this.refs.facetInputs) return;
    const checked = this.refs.facetInputs.filter((i) => i.checked);
    const status = this.closest('details')?.querySelector('facet-status');
    if (status instanceof FacetStatusComponent) status.updateListSummary(checked);
  }
}

if (!customElements.get('facet-inputs')) {
  customElements.define('facet-inputs', FacetInputsComponent);
}

// ── PriceFacetComponent ──

class PriceFacetComponent extends FCElement {
  #sliderObserver;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKey);
    this.#initSlider();
    this.#sliderObserver = new MutationObserver(() => {
      if (this.querySelector('.price-facet__slider[hidden]')) {
        requestAnimationFrame(() => this.#initSlider());
      }
    });
    this.#sliderObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKey);
    this.#sliderObserver?.disconnect();
  }

  #onKey = (e) => {
    if (e.metaKey) return;
    if (!/[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/.test(e.key)) {
      e.preventDefault();
    }
  };

  // ── Slider ──

  #initSlider() {
    // Query DOM directly — safe before FCElement ref update after morph
    const container = this.querySelector('.price-facet__slider');
    const sMin = this.querySelector('.price-facet__slider-input--min');
    const sMax = this.querySelector('.price-facet__slider-input--max');
    const range = this.querySelector('.price-facet__slider-range');
    const minInput = this.querySelector('[data-ref="minInput"]');
    const maxInput = this.querySelector('[data-ref="maxInput"]');

    if (!container || !sMin || !sMax || !maxInput) return;

    // Remove old listeners (idempotent — safe for re-init after morph)
    sMin.removeEventListener('input', this.#onSliderInput);
    sMax.removeEventListener('input', this.#onSliderInput);

    const rangeMax = Number(formatMoney(maxInput.getAttribute('data-max') ?? '0'));
    if (rangeMax <= 0) return;

    const step = rangeMax > 1000 ? Math.ceil(rangeMax / 500) : 1;
    sMin.min = sMax.min = 0;
    sMin.max = sMax.max = rangeMax;
    sMin.step = sMax.step = step;

    sMin.value = minInput?.value ? Number(formatMoney(minInput.value)) : 0;
    sMax.value = maxInput?.value ? Number(formatMoney(maxInput.value)) : rangeMax;

    const max = Number(sMax.max) || 1;
    range.style.left = `${(Number(sMin.value) / max) * 100}%`;
    range.style.right = `${100 - (Number(sMax.value) / max) * 100}%`;

    container.hidden = false;

    sMin.addEventListener('input', this.#onSliderInput);
    sMax.addEventListener('input', this.#onSliderInput);
  }

  #onSliderInput = (e) => {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    if (!sliderMin || !sliderMax || !minInput || !maxInput) return;

    let minVal = Number(sliderMin.value);
    let maxVal = Number(sliderMax.value);

    if (minVal > maxVal) {
      if (e.target === sliderMin) {
        sliderMin.value = maxVal;
        minVal = maxVal;
      } else {
        sliderMax.value = minVal;
        maxVal = minVal;
      }
    }

    minInput.value = minVal > 0 ? minVal : '';
    maxInput.value = maxVal < Number(sliderMax.max) ? maxVal : '';

    this.#updateSliderRange();
  };

  #updateSliderRange() {
    const { sliderMin, sliderMax, sliderRange } = this.refs;
    if (!sliderMin || !sliderMax || !sliderRange) return;

    const max = Number(sliderMax.max) || 1;
    const minPercent = (Number(sliderMin.value) / max) * 100;
    const maxPercent = (Number(sliderMax.value) / max) * 100;
    sliderRange.style.left = `${minPercent}%`;
    sliderRange.style.right = `${100 - maxPercent}%`;
  }

  #syncSliderFromInputs() {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const rangeMax = Number(sliderMax.max) || 0;
    sliderMin.value = minInput?.value ? Number(formatMoney(minInput.value)) : 0;
    sliderMax.value = maxInput?.value ? Number(formatMoney(maxInput.value)) : rangeMax;
    this.#updateSliderRange();
  }

  // ── Price filter logic ──

  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    this.#clamp(minInput);
    this.#clamp(maxInput);

    const facetsForm = this.closest('facets-form');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#syncConstraints();
    this.#syncSummary();
    this.#syncSliderFromInputs();
  }

  #clamp(input) {
    if (input.value.trim() === '') return;
    const val = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));
    if (val < min) input.value = min.toString();
    if (val > max) input.value = max.toString();
  }

  #syncConstraints() {
    const { minInput, maxInput } = this.refs;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  #syncSummary() {
    const { minInput, maxInput } = this.refs;
    const status = this.closest('details')?.querySelector('facet-status');
    if (status instanceof FacetStatusComponent) status.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet')) {
  customElements.define('price-facet', PriceFacetComponent);
}

// ── FacetClearComponent ──

class FacetClearComponent extends FCElement {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener(FCEvents.filterChange, this.#onFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FCEvents.filterChange, this.#onFilterUpdate);
  }

  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs, price-facet');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const status = event.target.closest('details')?.querySelector('facet-status');
    if (!(status instanceof FacetStatusComponent)) return;
    status.clearSummary();

    const facetsForm = this.closest('facets-form');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFilters();
  }

  #onKeyUp = (e) => {
    if (!e.metaKey && e.key === 'Enter') this.clearFilter(e);
  };

  #onFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    clearButton?.classList.toggle('facets__clear--active', event.shouldShowClearAll());
  };
}

if (!customElements.get('facet-clear')) {
  customElements.define('facet-clear', FacetClearComponent);
}

// ── FacetRemoveComponent ──

class FacetRemoveComponent extends FCElement {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(FCEvents.filterChange, this.#onFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FCEvents.filterChange, this.#onFilterUpdate);
  }

  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFiltersByURL(url);
  }

  #onFilterUpdate = (event) => {
    this.refs.clearButton?.classList.toggle('active', event.shouldShowClearAll());
  };
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemoveComponent);
}

// ── SortingFilterComponent ──

class SortingFilterComponent extends FCElement {
  requiredRefs = ['details', 'summary', 'listbox'];

  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!listbox) return;

    const options = [...listbox.querySelectorAll('[role="option"]')];
    const focused = options.find((o) => o.tabIndex === 0);
    const idx = focused ? options.indexOf(focused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#focus(options, Math.min(idx + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#focus(options, Math.max(idx - 1, 0));
        break;
      case 'Enter':
      case ' ': {
        const opt = event.target?.closest?.('[role="option"]');
        if (opt) {
          event.preventDefault();
          this.#pick(opt);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
    }
  };

  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!details || !summary) return;
    const open = details.open;
    summary.setAttribute('aria-expanded', String(open));
    if (open) {
      const sel = listbox?.querySelector('[aria-selected="true"]');
      if (sel instanceof HTMLElement) sel.focus();
    }
  };

  #focus(options, idx) {
    for (const o of options) {
      if (o instanceof HTMLElement) o.tabIndex = -1;
    }
    const target = options[idx];
    if (target instanceof HTMLElement) {
      target.tabIndex = 0;
      target.focus();
    }
  }

  #pick(option) {
    const input = option.querySelector('input[type="radio"]');
    if (!input || !(option instanceof HTMLElement)) return;
    for (const o of this.querySelectorAll('[role="option"]')) o.setAttribute('aria-selected', 'false');
    option.setAttribute('aria-selected', 'true');
    input.click();
    this.#close();
  }

  #close() {
    const { details, summary } = this.refs;
    if (!details) return;

    const options = this.querySelectorAll('[role="option"]');
    const selected = this.querySelector('[aria-selected="true"]');
    for (const o of options) {
      if (o instanceof HTMLElement) o.tabIndex = -1;
    }
    if (selected instanceof HTMLElement) selected.tabIndex = 0;

    details.open = false;
    if (summary instanceof HTMLElement) summary.focus();
  }

  updateFilterAndSorting(event) {
    const facetsForm = this.closest('facets-form') ?? this.closest('.shopify-section')?.querySelector('facets-form');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const mobile = window.innerWidth < 750;
    const dual = this.dataset.shouldUseSelectOnMobile === 'true';

    if (dual) {
      const selector = mobile ? 'input[name="sort_by"]' : 'select[name="sort_by"]';
      for (const el of this.querySelectorAll(selector)) el.disabled = true;
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    if (dual) {
      const selector = mobile ? 'input[name="sort_by"]' : 'select[name="sort_by"]';
      for (const el of this.querySelectorAll(selector)) el.disabled = false;
    }

    if (this.refs.details) this.refs.details.open = false;
  }

  updateFacetStatus(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;

    const details = this.querySelector('details');
    const status = details?.querySelector('[data-ref="sortStatus"]');
    if (!(details instanceof HTMLDetailsElement) || !(status instanceof HTMLElement)) return;

    let optionName = '';
    if (target instanceof HTMLSelectElement) {
      optionName = target.selectedOptions[0]?.textContent?.trim() ?? '';
    } else {
      optionName = target.dataset.optionName ?? '';
    }

    status.textContent = target.value !== details.dataset.defaultSortBy ? optionName : '';
  }
}

if (!customElements.get('sort-control')) {
  customElements.define('sort-control', SortingFilterComponent);
}

// ── FacetStatusComponent ──

class FacetStatusComponent extends FCElement {
  updateListSummary(checkedInputElements) {
    const count = checkedInputElements.length;
    this.getAttribute('facet-type') === 'swatches'
      ? this.#swatchSummary(checkedInputElements, count)
      : this.#bubbleSummary(checkedInputElements, count);
  }

  #swatchSummary(inputs, count) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (count === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (count > 3) {
      facetStatus.innerHTML = String(count);
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = inputs
      .map((el) => el.parentElement?.querySelector('span.swatch')?.outerHTML ?? '')
      .join('');
  }

  #bubbleSummary(inputs, count) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (count === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (count === 1) {
      facetStatus.innerHTML = inputs[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = String(count);
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  updatePriceSummary(minInput, maxInput) {
    const { facetStatus } = this.refs;
    if (!minInput.value && !maxInput.value) {
      facetStatus.innerHTML = '';
      return;
    }

    const lo = this.#toCents(minInput.value, '0');
    const hi = this.#toCents(maxInput.value, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#money(lo)}\u2013${this.#money(hi)}`;
  }

  #toCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fracStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let frac = parseInt(fracStr || '0', 10);
    frac = frac * Math.pow(10, 2 - frac.toString().length);
    return whole * 100 + frac;
  }

  #money(cents) {
    // Live input summaries update before section HTML returns; use the Liquid-published Shopify money formats.
    return formatFitMoney(cents, {
      scope: 'productItems',
    });
  }

  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status')) {
  customElements.define('facet-status', FacetStatusComponent);
}

bindFilterDrawerStateSync();
